import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Home, Trophy, Clock, Target, CheckCircle, XCircle, Star, Zap, Share2 } from 'lucide-react';
import useGameStore from '../store/gameStore';
import { mockMugshots, jailBirdBonusQuestions } from '../data/mockData';
import BirdMascot from '../components/BirdMascot';
import BirdAvatar from '../components/BirdAvatar';
import MockAdOverlay from '../components/MockAdOverlay';
import HalfwayModal from '../components/HalfwayModal';
import JailBirdBonusQuestion from '../components/JailBirdBonusQuestion';
import ProfileModal from '../components/ProfileModal';
import ShareButton from '../components/ShareButton';
import AchievementBadge from '../components/AchievementBadge';
import ReactionBar from '../components/ReactionBar';
import { Flame } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import confetti from 'canvas-confetti';

// Subtle gold confetti burst for rare (gold-tier) achievement unlocks
const fireGoldConfetti = () => {
  const colors = ['#FFD700', '#FDB931', '#FFF1A8', '#FFC400'];
  const defaults = { origin: { y: 0.3 }, colors, ticks: 200, gravity: 0.9, scalar: 0.9, disableForReducedMotion: true };
  confetti({ ...defaults, particleCount: 40, spread: 55, startVelocity: 35 });
  setTimeout(() => confetti({ ...defaults, particleCount: 25, spread: 80, startVelocity: 25 }), 200);
};

// Proxy mugshot images through our backend to bypass referrer restrictions (for Facebook in-app browser)
const getProxiedImageUrl = (originalUrl) => {
  if (!originalUrl) return '';
  // Check if it's from the sheriff's website
  if (originalUrl.includes('mecklenburgcountync.gov')) {
    return `${process.env.REACT_APP_BACKEND_URL}/api/mugshots/image-proxy?url=${encodeURIComponent(originalUrl)}`;
  }
  return originalUrl;
};

const GamePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    nickname,
    avatarId,
    addScore,
    incrementGamesPlayed,
    incrementCorrectGuesses,
    incrementJailBirdBonuses,
    addGameToHistory,
    soundEnabled
  } = useGameStore();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [gameActive, setGameActive] = useState(true);
  const [shuffledMugshots, setShuffledMugshots] = useState([]);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [showAd, setShowAd] = useState(false);
  const [showHalfway, setShowHalfway] = useState(false);
  const [timeBonus, setTimeBonus] = useState(0);
  const [showBonusQuestion, setShowBonusQuestion] = useState(false);
  const [bonusQuestion, setBonusQuestion] = useState(null);
  const [bonusQuestionShown, setBonusQuestionShown] = useState(false);
  const [bonusQuestionTrigger, setBonusQuestionTrigger] = useState(null);
  const [bonusQuestionCorrect, setBonusQuestionCorrect] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [gameStreak, setGameStreak] = useState(0);
  const [unlockedAchievements, setUnlockedAchievements] = useState([]);
  const goldConfettiFiredRef = useRef(false);

  // Fire a celebratory gold confetti burst when the Game Complete screen
  // reveals a gold-tier achievement badge (fires once).
  useEffect(() => {
    if (!gameActive && !goldConfettiFiredRef.current &&
        unlockedAchievements.some((a) => a.tier === 'gold')) {
      goldConfettiFiredRef.current = true;
      setTimeout(fireGoldConfetti, 400);
    }
  }, [gameActive, unlockedAchievements]);
  const [showPointsNotification, setShowPointsNotification] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const [TOTAL_QUESTIONS, setTOTAL_QUESTIONS] = useState(11);
  const [HALFWAY_POINT, setHALFWAY_POINT] = useState(6);

  // Initialize game - fetch real mugshot data
  useEffect(() => {
    const fetchGameData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mugshots/game`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch mugshot data');
        }
        
        const data = await response.json();
        
        if (!data.success || !data.mugshots || data.mugshots.length === 0) {
          // Fallback to mock data if API fails
          console.warn('API returned no data, using mock data as fallback');
          const allMugshots = [...mockMugshots, ...mockMugshots];
          const shuffled = allMugshots
            .sort(() => Math.random() - 0.5)
            .slice(0, TOTAL_QUESTIONS)
            .map(m => ({
              ...m,
              options: [...m.options].sort(() => Math.random() - 0.5)
            }));
          setShuffledMugshots(shuffled);
        } else {
          // Shuffle all available mugshots
          const shuffled = data.mugshots.sort(() => Math.random() - 0.5);
          
          // Handle case where we have 11 or 12 mugshots
          // If 12: use first 11 for game, 12th for bonus
          // If 11: use first 10 for game, 11th for bonus
          const totalAvailable = shuffled.length;
          const numRegularQuestions = totalAvailable >= 12 ? 11 : Math.max(10, totalAvailable - 1);
          
          // Format regular questions
          const formatted = shuffled.slice(0, numRegularQuestions).map(m => ({
            id: m.id,
            name: m.name,
            imageUrl: getProxiedImageUrl(m.mugshot_url),
            age: m.age,
            correctCharge: m.correct_charge,
            options: [m.correct_charge, ...m.wrong_charges].sort(() => Math.random() - 0.5)
          }));
          
          setShuffledMugshots(formatted);
          setTOTAL_QUESTIONS(numRegularQuestions); // Update total questions based on available data
          setHALFWAY_POINT(Math.floor(numRegularQuestions / 2)); // Adjust halfway point
          
          // Use the last mugshot as the bonus question (guaranteed unique)
          const bonusMugshot = shuffled[numRegularQuestions];
          if (bonusMugshot) {
            setBonusQuestion({
              id: bonusMugshot.id,
              name: bonusMugshot.name,
              imageUrl: getProxiedImageUrl(bonusMugshot.mugshot_url),
              age: bonusMugshot.age,
              correctCharge: bonusMugshot.correct_charge,
              options: [bonusMugshot.correct_charge, ...bonusMugshot.wrong_charges].sort(() => Math.random() - 0.5)
            });
          } else {
            // Fallback to mock bonus question if not enough records
            const randomBonusQ = jailBirdBonusQuestions[Math.floor(Math.random() * jailBirdBonusQuestions.length)];
            setBonusQuestion(randomBonusQ);
          }
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading game data:', error);
        setLoadError(error.message);
        
        // Fallback to mock data
        const allMugshots = [...mockMugshots, ...mockMugshots];
        const shuffled = allMugshots
          .sort(() => Math.random() - 0.5)
          .slice(0, TOTAL_QUESTIONS)
          .map(m => ({
            ...m,
            options: [...m.options].sort(() => Math.random() - 0.5)
          }));
        setShuffledMugshots(shuffled);
        
        // Set bonus question from mock data as fallback
        const randomBonusQ = jailBirdBonusQuestions[Math.floor(Math.random() * jailBirdBonusQuestions.length)];
        setBonusQuestion(randomBonusQ);
        
        setIsLoading(false);
      }
    };
    
    fetchGameData();
    
    // Randomly determine when to show bonus question (between questions 3 and 9)
    // Exclude: question 6 (halfway modal), question 4 (ad), question 8 (ad)
    const validBonusTriggers = [3, 5, 7, 9];
    const randomTrigger = validBonusTriggers[Math.floor(Math.random() * validBonusTriggers.length)];
    setBonusQuestionTrigger(randomTrigger);
  }, []);

  // Timer - counts UP instead of down (no timeout)
  useEffect(() => {
    if (!gameActive || showResult || shuffledMugshots.length === 0) return;

    const timer = setInterval(() => {
      setElapsedTime((prev) => prev + 0.1);
    }, 100);

    return () => clearInterval(timer);
  }, [gameActive, showResult, shuffledMugshots]);

  const handleOptionSelect = (option) => {
    if (showResult || !gameActive) return;
    setSelectedOption(option);
    
    // Auto-scroll to bottom to make submit button visible
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }, 100);
  };

  const calculateTimeBonus = (seconds) => {
    // Fast bonus tiers:
    // Under 3s: 100 bonus points
    // 3-5s: 75 bonus points
    // 5-8s: 50 bonus points
    // 8-12s: 25 bonus points
    // 12-20s: 10 bonus points
    // Over 20s: 0 bonus points
    
    if (seconds < 3) return 100;
    if (seconds < 5) return 75;
    if (seconds < 8) return 50;
    if (seconds < 12) return 25;
    if (seconds < 20) return 10;
    return 0;
  };

  const handleSubmit = () => {
    if (!selectedOption || showResult) return;

    const currentMugshot = shuffledMugshots[currentIndex];
    const isCorrect = selectedOption === currentMugshot.correctCharge;

    setShowResult(true);

    if (isCorrect) {
      const basePoints = 100; // Base points for correct answer
      const bonus = calculateTimeBonus(elapsedTime);
      const totalPoints = basePoints + bonus;
      
      setTimeBonus(bonus);
      setScore(score + totalPoints);
      setCorrectCount(correctCount + 1);
      incrementCorrectGuesses();
      
      // Show points notification
      setPointsEarned(totalPoints);
      setShowPointsNotification(true);
      
      // Hide notification after 1 second
      setTimeout(() => {
        setShowPointsNotification(false);
      }, 1000);
      
      setAnsweredQuestions(prev => [...prev, { correct: true, points: totalPoints, timeBonus: bonus }]);
    } else {
      setAnsweredQuestions(prev => [...prev, { correct: false }]);
      setTimeBonus(0);
    }

    // Auto-advance to next question after 2 seconds (give more time to see bonus)
    setTimeout(() => {
      handleNext();
    }, 2000);
  };

  const handleNext = () => {
    const nextIndex = currentIndex + 1;
    
    // Check for JailBird bonus question (show once randomly between questions 3-12)
    if (nextIndex === bonusQuestionTrigger && !bonusQuestionShown) {
      setShowBonusQuestion(true);
      return;
    }
    
    // Check for ad placements: after question 4 (index 3) and question 8 (index 7)
    if (nextIndex === 4 || nextIndex === 8) {
      setShowAd(true);
      return;
    }
    
    // Check for halfway modal after question 8 (index 7 completed, moving to index 8)
    if (nextIndex === HALFWAY_POINT && !showHalfway) {
      setShowHalfway(true);
      return;
    }
    
    proceedToNext();
  };
  
  const handleBonusAnswer = (isCorrect) => {
    setBonusQuestionShown(true);
    setShowBonusQuestion(false);
    
    if (isCorrect) {
      const bonusPoints = 500;
      setScore(score + bonusPoints);
      setBonusQuestionCorrect(true);
      incrementJailBirdBonuses();
    }
    
    // Continue to next question after short delay
    setTimeout(() => {
      proceedToNext();
    }, 500);
  };

  const proceedToNext = () => {
    console.log('🎯 proceedToNext() called');
    console.log('  - Current Index:', currentIndex);
    console.log('  - Total Questions:', shuffledMugshots.length);
    console.log('  - Is Last Question?', currentIndex >= shuffledMugshots.length - 1);
    
    if (currentIndex < shuffledMugshots.length - 1) {
      console.log('  ➡️ Moving to next question');
      setCurrentIndex(currentIndex + 1);
      setSelectedOption(null);
      setShowResult(false);
      setElapsedTime(0);
      setTimeBonus(0);
    } else {
      console.log('  🎬 GAME COMPLETE! Finishing game...');
      // Go straight to game completion (no end-game ad)
      finishGame();
    }
  };

  const handleAdClose = () => {
    console.log('📺 handleAdClose() called');
    
    setShowAd(false);
    console.log('  ➡️ Ad closed - continuing to next question');
    proceedToNext();
  };

  const handleHalfwayContinue = () => {
    setShowHalfway(false);
    proceedToNext();
  };

  const finishGame = async () => {
    console.log('🎮 finishGame() called - Starting game completion...');
    console.log('  - Nickname:', nickname);
    console.log('  - Avatar ID:', avatarId);
    console.log('  - Score:', score);
    console.log('  - Correct Count:', correctCount);
    console.log('  - Total Questions:', shuffledMugshots.length);
    
    setGameActive(false);
    const accuracy = Math.round((correctCount / shuffledMugshots.length) * 100);
    
    console.log('  - Calculated Accuracy:', accuracy);
    console.log('  - Bonus Question Correct:', bonusQuestionCorrect);
    console.log('  - About to submit to:', `${process.env.REACT_APP_BACKEND_URL}/api/scores/submit`);
    
    // Submit score to backend FIRST
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nickname,
          avatar_id: avatarId,
          score,
          correct_count: correctCount,
          total_questions: shuffledMugshots.length,
          accuracy,
          jailbird_bonus_won: bonusQuestionCorrect
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Score submitted successfully:', result);
        
        // Capture streak & newly unlocked achievements for the results screen
        setGameStreak(result.current_streak || 0);
        setUnlockedAchievements(result.newly_unlocked || []);

        // Alert the player about each newly completed achievement
        (result.newly_unlocked || []).forEach((ach, idx) => {
          setTimeout(() => {
            toast({
              title: `🏆 Achievement Unlocked!`,
              description: `${ach.name} — ${ach.description}`,
            });
          }, idx * 600);
        });
        
        // Update local store immediately
        addScore(score);
        incrementGamesPlayed();
        addGameToHistory({
          date: new Date().toISOString(),
          score,
          correctCount,
          totalQuestions: shuffledMugshots.length,
          accuracy
        });
        
        // Dispatch custom event to force all pages to refresh
        window.dispatchEvent(new CustomEvent('gameCompleted', { 
          detail: { nickname, score } 
        }));
      } else {
        // Read response body once
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = errorText;
        }
        console.error('❌ Failed to submit score:', response.status, errorData);
        // Still update local store as fallback
        addScore(score);
        incrementGamesPlayed();
        addGameToHistory({
          date: new Date().toISOString(),
          score,
          correctCount,
          totalQuestions: shuffledMugshots.length,
          accuracy
        });
      }
    } catch (error) {
      console.error('❌ Error submitting score:', error);
      // Update local store as fallback
      addScore(score);
      incrementGamesPlayed();
      addGameToHistory({
        date: new Date().toISOString(),
        score,
        correctCount,
        totalQuestions: shuffledMugshots.length,
        accuracy
      });
    }
  };

  if (isLoading || shuffledMugshots.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
        <p className="text-muted-foreground">Loading game data...</p>
      </div>
    );
  }

  const currentMugshot = shuffledMugshots[currentIndex];
  const progress = ((currentIndex + 1) / shuffledMugshots.length) * 100;

  // Game Over Screen
  if (!gameActive) {
    const accuracy = Math.round((correctCount / shuffledMugshots.length) * 100);
    
    // Bird-themed completion messages based on performance
    const getCompletionMessage = () => {
      if (accuracy >= 90) {
        return [
          'Top of the pecking order! You are a master detective!',
          'Spread your wings and fly! Absolutely legendary performance!',
          'This bird has earned its freedom! Outstanding work!',
          'You did not just fly the coop, you soared! Incredible!'
        ][(score + correctCount) % 4];
      } else if (accuracy >= 70) {
        return [
          'Solid detective work! Your feathers are looking sharp!',
          'Nice flying! You are no sitting duck!',
          'Well done! You are earning your detective badge!',
          'Good job! Your instincts are sharp as a beak!'
        ][(score + correctCount) % 4];
      } else if (accuracy >= 50) {
        return [
          'Not bad... for a chicken. Room for improvement!',
          'You are flying in circles. Try focusing next time!',
          'Meh. Even a broken clock is right twice a day.',
          'Could be worse... but could definitely be better!'
        ][(score + correctCount) % 4];
      } else {
        return [
          'Did you peck at the answers with your eyes closed?',
          'This bird needs flying lessons. Seriously.',
          'Back to detective school for you, birdbrain!',
          'Even a penguin could score higher... and they cannot fly!'
        ][(score + correctCount) % 4];
      }
    };
    
    const completionMessage = getCompletionMessage();
    
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-2xl"
        >
          <Card className="p-6 sm:p-8 md:p-12 text-center bg-gradient-card border-border">
            {/* Player Avatar and Nickname */}
            <div className="flex flex-col items-center mb-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
                className="mb-3"
              >
                <BirdAvatar
                  avatarId={avatarId}
                  size={100}
                  animate={true}
                />
              </motion.div>
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gradient-primary mb-2">
                {nickname}
              </h2>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold mb-3 sm:mb-4">
              Game Complete!
            </h1>
            
            <p className="text-lg sm:text-xl text-primary font-semibold mb-4 px-4">
              {completionMessage}
            </p>
            
            <p className="text-lg sm:text-xl text-muted-foreground mb-8">
              You scored <span className="text-gradient-primary font-bold text-2xl sm:text-3xl">{score}</span> points!
            </p>
            
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 sm:mb-8">
              <div className="p-3 sm:p-4 rounded-lg bg-muted/50">
                <div className="text-2xl sm:text-3xl font-heading font-bold text-gradient-primary mb-1">{correctCount}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">Correct</div>
              </div>
              <div className="p-3 sm:p-4 rounded-lg bg-muted/50">
                <div className="text-2xl sm:text-3xl font-heading font-bold text-gradient-accent mb-1">{shuffledMugshots.length - correctCount}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">Wrong</div>
              </div>
              <div className="p-3 sm:p-4 rounded-lg bg-muted/50">
                <div className="text-2xl sm:text-3xl font-heading font-bold text-gradient-gold mb-1">{accuracy}%</div>
                <div className="text-xs sm:text-sm text-muted-foreground">Accuracy</div>
              </div>
            </div>
            
            {/* Daily Streak Banner */}
            {gameStreak >= 2 && (
              <motion.div
                data-testid="streak-banner"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-orange-500/20 to-red-500/10 border border-orange-500/40"
              >
                <Flame className="w-6 h-6 text-orange-400" />
                <span className="font-heading font-bold text-orange-300 text-lg">
                  {gameStreak}-Day Streak!
                </span>
                <span className="text-sm text-muted-foreground hidden sm:inline">Keep it going!</span>
              </motion.div>
            )}

            {/* Newly Unlocked Achievements */}
            {unlockedAchievements.length > 0 && (
              <div data-testid="unlocked-achievements" className="mb-6">
                <p className="text-sm font-semibold text-gold mb-3 flex items-center justify-center gap-2">
                  <Star className="w-4 h-4" />
                  {unlockedAchievements.length === 1 ? 'Achievement Unlocked!' : `${unlockedAchievements.length} Achievements Unlocked!`}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {unlockedAchievements.map((ach) => (
                    <AchievementBadge key={ach.id} achievement={ach} unlocked animate />
                  ))}
                </div>
              </div>
            )}

            {/* Social Share Button */}
            <div className="mb-6">
              <p className="text-sm text-muted-foreground mb-3 flex items-center justify-center gap-2">
                <Share2 className="w-4 h-4" />
                Share your score with friends!
              </p>
              <div className="flex justify-center">
                <ShareButton
                  nickname={nickname}
                  avatarId={avatarId}
                  totalScore={score}
                  gamesPlayed={useGameStore.getState().gamesPlayed || 1}
                  accuracy={accuracy}
                  variant="default"
                  size="lg"
                  className="bg-gradient-primary hover-glow-primary text-primary-foreground"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => window.location.reload()}
                className="bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold text-sm sm:text-base py-3 sm:py-4"
              >
                Play Again
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/leaderboard')}
                className="border-primary/30 hover:bg-primary/10 text-sm sm:text-base py-3 sm:py-4"
              >
                <Trophy className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                <span className="hidden sm:inline">View </span>Leaderboard
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-2 sm:p-4">
      {/* Profile Modal */}
      <ProfileModal 
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />
      
      {/* JailBird Bonus Question */}
      {showBonusQuestion && bonusQuestion && (
        <JailBirdBonusQuestion
          question={bonusQuestion}
          onAnswer={handleBonusAnswer}
          score={score}
          correctCount={correctCount}
          totalQuestions={TOTAL_QUESTIONS}
          nickname={nickname}
          avatarId={avatarId}
        />
      )}
      
      {/* Ad Overlay */}
      {showAd && <MockAdOverlay onClose={handleAdClose} nickname={nickname} />}
      
      {/* Halfway Modal */}
      {showHalfway && (
        <HalfwayModal
          score={score}
          correctCount={correctCount}
          totalQuestions={TOTAL_QUESTIONS}
          nickname={nickname}
          avatarId={avatarId}
          onContinue={handleHalfwayContinue}
        />
      )}
      
      {/* Header - Sticky */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border/50 pb-3 sm:pb-4 mb-4 sm:mb-6">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-center justify-between mb-3 sm:mb-4 pt-3 sm:pt-4">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-muted-foreground hover:text-foreground text-sm px-2 sm:px-4"
              size="sm"
            >
              <Home className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Home
            </Button>
            
            {nickname && (
              <button
                onClick={() => setShowProfileModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20 hover:bg-primary/20 hover:border-primary/30 transition-all cursor-pointer"
              >
                <BirdAvatar avatarId={avatarId} size={20} />
                <span className="text-xs sm:text-sm font-semibold text-primary">{nickname}</span>
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2 md:gap-4">
            <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg bg-card border border-border">
              <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-gold" />
              <span className="font-semibold text-xs sm:text-sm md:text-base">{score}</span>
            </div>
            
            <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg bg-card border border-border">
              <Target className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
              <span className="font-semibold text-xs sm:text-sm md:text-base">{correctCount}/{shuffledMugshots.length}</span>
            </div>
            
            <div className="hidden md:flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg bg-gold/10 border border-gold/20">
              <Zap className="w-4 h-4 text-gold" />
              <span className="text-xs text-gold font-medium">Speed = Bonus!</span>
            </div>
          </div>
        </div>
        
          {/* Progress */}
          <div className="space-y-1.5 sm:space-y-2">
            <div className="flex justify-between text-xs sm:text-sm text-muted-foreground">
              <span>Question {currentIndex + 1} of {shuffledMugshots.length}</span>
              <span className="hidden sm:inline">{Math.round(progress)}% Complete</span>
              <span className="sm:hidden">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-1.5 sm:h-2" />
          </div>
        </div>
      </div>

      {/* Game Content */}
      <div className="container mx-auto max-w-4xl relative pb-6">

        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="p-4 sm:p-6 md:p-8 bg-gradient-card border-border overflow-visible relative">
              {/* Points Notification - Celebration Popup */}
              <AnimatePresence>
                {showPointsNotification && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.5, y: -10 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    className="absolute left-1/2 -translate-x-1/2 z-50"
                    style={{ bottom: '30%' }}
                  >
                    <div className="flex items-center gap-2 px-5 sm:px-7 py-2.5 sm:py-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-2xl border-2 border-green-400">
                      <span className="text-xl sm:text-2xl">🎉</span>
                      <span className="font-bold text-lg sm:text-xl">Correct! +{pointsEarned} points</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Timer and Bonus Info */}
              <div className="flex flex-col items-center justify-center mb-4 sm:mb-6 gap-2 sm:gap-3">
                <div className={`flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-full ${
                  elapsedTime < 5 ? 'bg-success/20 text-success' : 
                  elapsedTime < 12 ? 'bg-primary/10 text-primary' : 
                  'bg-muted/50 text-muted-foreground'
                } transition-colors`}>
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-xl sm:text-2xl font-heading font-bold">{elapsedTime.toFixed(1)}s</span>
                </div>
                
                {/* Speed bonus indicator */}
                {!showResult && (
                  <div className="text-xs sm:text-sm text-center space-y-1">
                    <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                      <Zap className={`w-3 h-3 sm:w-4 sm:h-4 ${
                        elapsedTime < 3 ? 'text-gold' :
                        elapsedTime < 5 ? 'text-success' :
                        elapsedTime < 8 ? 'text-primary' :
                        elapsedTime < 12 ? 'text-accent' :
                        'text-muted-foreground'
                      }`} />
                      <span className={`font-semibold ${
                        elapsedTime < 3 ? 'text-gold' :
                        elapsedTime < 5 ? 'text-success' :
                        elapsedTime < 8 ? 'text-primary' :
                        elapsedTime < 12 ? 'text-accent' :
                        'text-muted-foreground'
                      }`}>
                        <span className="hidden sm:inline">Speed Bonus: </span>+{calculateTimeBonus(elapsedTime)} pts
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {elapsedTime < 3 ? '🔥 Lightning fast!' :
                       elapsedTime < 5 ? '⚡ Very fast!' :
                       elapsedTime < 8 ? '✨ Fast!' :
                       elapsedTime < 12 ? '👍 Good pace' :
                       elapsedTime < 20 ? '⏱️ Take your time' :
                       '🐌 No rush!'}
                    </div>
                  </div>
                )}
                
                {/* Show earned bonus after answer */}
                {showResult && timeBonus > 0 && (
                  <div className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-gold/20 text-gold animate-pulse">
                    <Zap className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="font-semibold text-sm sm:text-base">+{timeBonus} Speed Bonus!</span>
                  </div>
                )}
              </div>

              {/* Mugshot */}
              <div className="mb-4 sm:mb-6">
                <div className="aspect-square max-w-xs sm:max-w-md mx-auto rounded-lg sm:rounded-xl overflow-hidden border-2 sm:border-4 border-border shadow-xl sm:shadow-2xl">
                  <img
                    src={currentMugshot.imageUrl}
                    alt="Mugshot"
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="text-center mt-3 sm:mt-4">
                  <h2 className="text-xl sm:text-2xl font-heading font-bold mb-1">{currentMugshot.name}</h2>
                  <p className="text-sm sm:text-base text-muted-foreground">Age: {currentMugshot.age}</p>
                </div>

                {/* Emoji reactions */}
                {currentMugshot.id && (
                  <ReactionBar recordId={currentMugshot.id} nickname={nickname} />
                )}
              </div>

              {/* Question */}
              <h3 className="text-lg sm:text-xl md:text-2xl font-heading font-semibold text-center mb-4 sm:mb-6 px-2">
                What crime was <span className="text-gradient-primary">{currentMugshot.name.includes(',') ? currentMugshot.name.split(',')[1].trim().split(' ')[0] : currentMugshot.name.split(' ')[0]}</span> arrested for?
              </h3>

              {/* Options */}
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                {currentMugshot.options.map((option, index) => {
                  const isSelected = selectedOption === option;
                  const isCorrect = option === currentMugshot.correctCharge;
                  
                  // After submitting, always show correct answer in green
                  const showCorrect = showResult && isCorrect;
                  // After submitting, show wrong selected answer in red
                  const showWrong = showResult && isSelected && !isCorrect;

                  return (
                    <motion.button
                      key={index}
                      onClick={() => handleOptionSelect(option)}
                      disabled={showResult}
                      whileHover={{ scale: showResult ? 1 : 1.02 }}
                      whileTap={{ scale: showResult ? 1 : 0.98 }}
                      className={`p-3 sm:p-4 rounded-lg border-2 text-left font-medium transition-all text-sm sm:text-base ${
                        showCorrect
                          ? 'bg-green-500/30 border-green-500 text-green-100 font-bold'
                          : showWrong
                          ? 'bg-red-500/30 border-red-500 text-red-100 font-bold'
                          : isSelected
                          ? 'bg-primary/20 border-primary text-primary'
                          : 'bg-card border-border hover:border-primary/50 hover:bg-primary/5'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="pr-2">{option}</span>
                        {showCorrect && <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />}
                        {showWrong && <XCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />}
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Action Button */}
              {!showResult && (
                <Button
                  size="lg"
                  onClick={handleSubmit}
                  disabled={!selectedOption}
                  className="w-full bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold text-base sm:text-lg py-3 sm:py-4"
                >
                  Submit Answer
                </Button>
              )}
            </Card>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default GamePage;
